import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'
import App from '@/App.vue'
import router from '@/router/index'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createBootstrap } from 'bootstrap-vue-next'
import Vue3Tour from 'vue3-tour'
import VueGtag from 'vue-gtag'
import VueGtm from '@gtm-support/vue-gtm'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import VueMatomo from 'vue-matomo'
import { vue3Debounce } from 'vue-debounce'
import FirebaseVue from '@/utils/firebase.ts'
import MauticVue from '@/utils/mautic.ts'

const isProd = import.meta.env.MODE === 'production'
const isStage = import.meta.env.MODE === 'staging'
const isValidGtmId = /^(GTM|G)-[0-9A-Z]+$/
const isValidGaId = /^(G|GT|UA)-[A-Z0-9-]+$/
const gtmId = import.meta.env.VITE_GTM
const gaTrackingId = import.meta.env.VITE_GA_TRACING

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(App)

app.use(pinia)
app.use(router)

if (isProd || isStage) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY,
    environment: import.meta.env.VITE_SENTRY_ENV
  })
}

app.use(createBootstrap())
app.use(Vue3Tour)

if (isValidGaId.test(gaTrackingId)) {
  app.use(VueGtag, { config: { id: gaTrackingId } }, router)
} else {
  console.warn('Skipping Google Analytics setup: invalid or missing VITE_GA_TRACING.')
}

if (isValidGtmId.test(gtmId)) {
  app.use(VueGtm, {
    id: gtmId,
    enabled: false,
    debug: !isProd,
    loadScript: true,
    vueRouter: router
  })
} else {
  console.warn('Skipping Google Tag Manager setup: invalid or missing VITE_GTM.')
}

app.use(VueReCaptcha, { siteKey: import.meta.env.VITE_RECAPTCHA, loaderOptions: {} })
app.use(VueMatomo, {
  router,
  host: 'https://piwik.infodiagram.com',
  siteId: import.meta.env.VITE_MAUTIC_SITE_ID,
  enableHeartBeatTimer: true,
  debug: !isProd,
  cookieDomain: import.meta.env.VITE_MAUTIC_COOKIE_DOMAIN,
  domains: import.meta.env.VITE_MAUTIC_DOMAINS,
  requireConsent: false,
  requireCookieConsent: false
})
app.directive('debounce', vue3Debounce({ lock: true }))
app.use(FirebaseVue)

if (isProd || isStage) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  app.use(MauticVue as any, {
    trackerScript: 'https://clients.infodiagram.com/mtc.js',
    router,
    pinia
  })
}

// Manual Hotjar snippet
if (isProd) {
  const hjScript = document.createElement('script')
  hjScript.innerHTML = `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:2961063,hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
  document.head.appendChild(hjScript)
}

app.config.errorHandler = async (err, vm, info) => {
  console.error('errorCaptured', err, vm, info)
}

app.mount('#app')
